"use client";

import type React from "react";
import { forwardRef, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { parseDurationInput } from "@/lib/duration";
import { formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";

interface DurationInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
    value: number | null | undefined; // Total minutes
    onChange?: (minutes: number | null) => void;
    /**
     * How the committed value is rendered when the field is not being typed into.
     * Defaults to the duration shape ("1h 30m"); the timer panel passes a clock
     * formatter so the editor opens showing exactly what the row showed. Either shape
     * parses back, whichever one is displayed.
     */
    formatValue?: (minutes: number | null) => string;
}

export const DurationInput = forwardRef<HTMLInputElement, DurationInputProps>(
    ({ value = 0, onChange, onBlur, onFocus, className, formatValue = formatDuration, ...props }, ref) => {
        const [displayValue, setDisplayValue] = useState("");
        const [isEditing, setIsEditing] = useState(false);
        const [_hasError, setHasError] = useState(false);

        // Update display value when value prop changes
        useEffect(() => {
            if (!isEditing) {
                setDisplayValue(formatValue(value));
                setHasError(false);
            }
        }, [value, isEditing, formatValue]);

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsEditing(true);
            onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setIsEditing(false);

            handlePersistChange();

            onBlur?.(e);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setDisplayValue(e.target.value);
            setHasError(false);
        };

        const handlePersistChange = () => {
            const parsedMinutes = parseDurationInput(displayValue);

            if (displayValue.trim() === "") {
                setHasError(false);
                onChange?.(null);
                setIsEditing(false);
            } else if (parsedMinutes !== null) {
                setHasError(false);
                const formattedValue = formatValue(parsedMinutes);
                setDisplayValue(formattedValue);
                onChange?.(parsedMinutes);
                setIsEditing(false);
            } else if (displayValue.trim() !== "") {
                setHasError(true);
            } else {
                // Empty input
                setHasError(false);
                setDisplayValue("0m");
                onChange?.(null);
                setIsEditing(false);
            }
        };

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            // Explicitly allow space key - stop propagation to prevent parent handlers from interfering
            if (e.key === " ") {
                e.stopPropagation();
                // Don't prevent default - allow space to be typed normally
                return;
            }

            if (e.key === "Enter") {
                e.preventDefault();

                handlePersistChange();
                return;
            }

            // Call parent's onKeyDown if provided (for all other keys)
            props.onKeyDown?.(e);
        };

        return (
            <Input
                {...props}
                ref={ref}
                type="text"
                value={displayValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder="e.g., 1d 3h 30m or 1:33:00"
                // Merged, not replaced: callers size this input to fit the row it sits in.
                className={cn("focus-visible:ring focus-visible:ring-neutral-600 border-none", className)}
            />
        );
    }
);

DurationInput.displayName = "DurationInput";
